import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  DialogTitleCustom,
  Title,
} from "layouts/Sidenav/AccountInfo/ModalChangeAvatar/style";

import { ReactComponent as IconClose } from "assets/svg/20-close.svg";
import { ReactComponent as IconUpload } from "assets/svg/20-upload.svg";
import { ReactComponent as IconDownload } from "assets/svg/20-download.svg";

import { DialogActionWrapper } from "modules/DeclareLeave/components/CustomCalendarTimeKeeping/style";
import { useDispatch, useSelector } from "react-redux";
import {
  useDialog,
  useFormUpdate,
  useFormValidation,
  useLegacyEffect,
  useRefsManager,
} from "helper/customHook";
import { DIALOG } from "helper/enum/dialogEnum";
import { isOpenDialogSelector } from "redux-store/static/redux/selector";
import FormDatePicker from "components/common/Form/FormDatepicker";
import pxToRem from "theme/base/pxToRem";
import FormUpload from "components/common/Form/FormUpload";
import { UploadTitle } from "modules/PersonnelInformation/Pages/Main/component/modal/style";
import {
  formInputImportSelector,
  timeConfirmPeriodSelector,
} from "modules/DeclareLeave/redux/selector";
import {
  importTimeKeepingStart,
  resetFormInputImport,
  updateFormImportTimeKeeping,
} from "modules/DeclareLeave/redux/importTimeKeeping";
import { schemasFormImport } from "./schemas";
import { exportTemplateTimeKeepingStart } from "modules/DeclareLeave/redux/exportTemplateTimeKeeping";
import moment from "moment";
import { SCREEN } from "helper/roleChecker/Screens";
import { getTimeConfirmPeriodStart } from "modules/DeclareLeave/redux/getTimeConfirmPeriod";

const ModalImport = () => {
  const importDialog = useDialog(DIALOG.IMPORT_TIMEKEEPING);
  const dispatch = useDispatch();
  const open = useSelector(isOpenDialogSelector(DIALOG.IMPORT_TIMEKEEPING));
  const formData = useSelector(formInputImportSelector);

  const { attachRef, elementsRef } = useRefsManager();

  const { formErrors, clearFieldError, validateForm, clearErrors } =
    useFormValidation(formData, schemasFormImport, elementsRef);

  const timeConfirm = useSelector(timeConfirmPeriodSelector);
  const isOutOfTimeConfirm =
    moment().isAfter(moment(timeConfirm?.toDate, "YYYY-MM-DD")) ||
    moment().isBefore(moment(timeConfirm?.fromDate, "YYYY-MM-DD"));

  const { handleFileChange, handleDatePickerChange } = useFormUpdate(
    updateFormImportTimeKeeping,
    (event) => {
      clearFieldError(event);
    }
  );

  const handleClose = () => {
    clearErrors();
    dispatch(resetFormInputImport());
    importDialog.close();
  };

  const handleSubmit = () => {
    validateForm(() => {
      dispatch(importTimeKeepingStart(handleClose));
    });
  };

  const handleExportTemplate = () => {
    dispatch(exportTemplateTimeKeepingStart());
  };

  useLegacyEffect(() => {
    const month = formData.month ?? moment(formData.monthSelected).month() + 1;
    const year = formData.year ?? moment(formData.monthSelected).year();
    dispatch(
      getTimeConfirmPeriodStart({
        month,
        year,
        screenCode: SCREEN.IP_021,
      })
    );
  }, [formData.month, formData.year, dispatch, formData.monthSelected]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitleCustom sx={{ width: "100%" }}>
        <Title>Import chấm công</Title>
        <IconButton onClick={handleClose}>
          <IconClose />
        </IconButton>
      </DialogTitleCustom>

      <DialogContent
        sx={(theme) => ({
          padding: `${pxToRem(24)} ${pxToRem(20)}`,
        })}
      >
        <Stack
          direction={"column"}
          justifyContent={"center"}
          // alignItems={"center"}
          gap={pxToRem(16)}
        >
          <FormDatePicker
            name={`monthSelected`}
            formData={formData}
            formErrors={formErrors}
            label="Kì chấm công"
            attachRef={attachRef}
            format="MM/YYYY"
            views={["month", "year"]}
            onChange={handleDatePickerChange}
          />
          <FormUpload
            name={`file`}
            buttonProps={{
              fullWidth: true,
              variant: "blur",
              sx: (theme) => ({
                background: "#fff",
                boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.10)",
                borderRadius: pxToRem(8),
                border: `1px dashed ${theme.palette.stroke.strokeBrand}`,
                cursor: "pointer",
                transition: "all 0.3s ease, visibility 0.2s ease",
                minHeight: pxToRem(120),
                width: "100%",
              }),
            }}
            previewStyle={{
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
              background: "#E4EBFF",
              padding: "10px",
            }}
            formUploadProps={{
              accept: [".xlsx"],
            }}
            title={
              <Stack
                direction={"column"}
                justifyContent={"center"}
                alignItems={"center"}
                gap={pxToRem(4)}
              >
                <IconUpload />
                <UploadTitle>Import file chấm công (Excel)</UploadTitle>
              </Stack>
            }
            onChange={handleFileChange}
            formData={formData}
            formErrors={formErrors}
            attachRef={attachRef}
          />

          <Box
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            width={"100%"}
            marginTop={"-40px"}
          >
            {isOutOfTimeConfirm && (
              <Typography
                sx={(theme) => ({
                  fontSize: theme.typography.size.sm_14,
                  color: theme.palette.text.textWarning,
                  marginBottom: pxToRem(10),
                })}
              >
                Cảnh báo import sau thời gian chốt công của CBNV, vui lòng xác
                nhận chắn chắn muốn import.
              </Typography>
            )}
            <Button
              variant="blur"
              onClick={handleExportTemplate}
              sx={(theme) => ({
                background: theme.palette.background.bgSecondary,
                color: theme.palette.text.textPrimary,
                fontWeight: "400",
                mr: 1,
                width: pxToRem(180),
                fontSize: pxToRem(14),
              })}
            >
              Tải xuống file mẫu{" "}
              <IconDownload style={{ marginLeft: pxToRem(10) }} />
            </Button>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <DialogActionWrapper>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData[`file_files`]!?.length}
            sx={(theme) => ({
              mr: 1,
            })}
          >
            Xác nhận
          </Button>
        </DialogActionWrapper>
      </DialogActions>
    </Dialog>
  );
};

export default ModalImport;
